import { useEffect, useRef, useState } from "react";
import { ArrowLeft, MessagesSquare, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { card, ROLE } from "@/components/club/ui";

const hhmm = (iso) => new Date(iso).toLocaleString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

const Thread = ({ group, onBack }) => {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const lastRef = useRef(null);

  useEffect(() => {
    let timer;
    let alive = true;
    lastRef.current = null;
    setMsgs([]);
    const poll = async () => {
      try {
        const { data } = await api.get(`/club/groups/${group.id}/messages`, { params: lastRef.current ? { after: lastRef.current } : {} });
        if (alive && data.length) {
          lastRef.current = data[data.length - 1].created_at;
          setMsgs((cur) => [...cur, ...data.filter((d) => !cur.some((c) => c.id === d.id))]);
        }
      } catch { /* keep polling */ }
      if (alive) timer = setTimeout(poll, 4000);
    };
    poll();
    return () => { alive = false; clearTimeout(timer); };
  }, [group.id]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [msgs.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/club/groups/${group.id}/messages`, { text });
      lastRef.current = data.created_at;
      setMsgs((cur) => [...cur, data]);
      setText("");
    } catch (x) { toast.error(errMsg(x)); } finally { setSending(false); }
  };
  const remove = async (m) => {
    if (!window.confirm("¿Borrar este mensaje?")) return;
    try { await api.delete(`/club/messages/${m.id}`); setMsgs((cur) => cur.filter((c) => c.id !== m.id)); } catch (x) { toast.error(errMsg(x)); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 flex flex-col h-[70vh] overflow-hidden" data-testid="chat-thread">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 bg-[#0B1D3A] text-white">
        <button onClick={onBack} className="p-1" aria-label="Volver" data-testid="chat-back"><ArrowLeft size={20} /></button>
        <p className="font-bold text-lg">{group.name}</p>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" data-testid="chat-messages">
        {!msgs.length && <p className="text-center text-slate-500 mt-10">Todavía no hay mensajes. ¡Escribe el primero!</p>}
        {msgs.map((m) => {
          const mine = m.user_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${mine ? "bg-[#3D88B8] text-white rounded-br-sm" : "bg-white border border-slate-200 rounded-bl-sm"}`}>
                {!mine && <p className="text-xs font-bold text-[#B23A38]">{m.name}{m.role !== "member" ? ` · ${ROLE[m.role]}` : ""}</p>}
                <p className="whitespace-pre-line break-words">{m.text}</p>
                <p className={`text-[11px] mt-1 flex items-center gap-2 ${mine ? "text-blue-100" : "text-slate-400"}`}>{hhmm(m.created_at)}
                  {m.can_delete && <button onClick={() => remove(m)} aria-label="Borrar" className="hover:text-[#B23A38]" data-testid={`chat-delete-${m.id}`}><Trash2 size={13} /></button>}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex gap-2 p-3 pr-20 sm:pr-3 border-t border-slate-200 bg-white">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder="Escribe un mensaje…" className="flex-1 min-w-0 rounded-full px-4" data-testid="chat-input" />
        <button disabled={sending || !text.trim()} className="w-12 h-12 shrink-0 rounded-full bg-[#B23A38] hover:bg-[#962F2D] text-white grid place-items-center disabled:opacity-50 transition-colors" aria-label="Enviar" data-testid="chat-send"><Send size={20} /></button>
      </form>
    </div>
  );
};

export const GroupChat = ({ clubId, initial }) => {
  const [groups, setGroups] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    api.get(`/club/clubs/${clubId}/chats`).then((r) => { setGroups(r.data); if (initial) setOpen(r.data.find((g) => g.id === initial) || null); }).catch(() => setGroups([]));
  }, [clubId, initial]);
  if (open) return <Thread group={open} onBack={() => setOpen(null)} />;
  if (!groups) return <p className="text-slate-500">Cargando…</p>;
  if (!groups.length) return <p className={`${card} text-slate-600`} data-testid="chat-empty">No estás en ningún grupo. Pide a tu club la clave de tu grupo e introdúcela en FAUNNY Club para entrar en su chat.</p>;
  return (
    <ul className="space-y-2" data-testid="chat-groups">{groups.map((g) => (
      <li key={g.id}><button onClick={() => setOpen(g)} className={`${card} w-full text-left flex items-center gap-4 hover:border-[#3D88B8] transition-colors`} data-testid={`chat-group-${g.id}`}>
        <span className="w-12 h-12 rounded-full bg-[#EAF3F9] text-[#3D88B8] grid place-items-center shrink-0"><MessagesSquare size={22} /></span>
        <span className="flex-1 min-w-0"><b className="block text-lg text-[#0B1D3A]">{g.name}</b>
          <span className="block text-sm text-slate-500 truncate">{g.last ? `${g.last.name}: ${g.last.text}` : "Sin mensajes todavía"}</span></span>
      </button></li>))}</ul>
  );
};
